import React, { useEffect, useState } from "react";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import {
  UnreleasedReleaseVehicleFileViewModel,
  releaseAPI,
} from "../api/releaseAPI";
import DateDisplay from "../common/DateDisplay";
import { Card, Col, Container, Row, Table } from "react-bootstrap";
import { Problem } from "../api/backendApi";
import {
  DataFileTypeModel,
  VehicleFileValidationTypeModel,
  vehicleFileAPI,
} from "../api/vehicleFileAPI";
import VehicleFileValidator from "../common/VehicleFileValidator";
import { useRoutablePages } from "../common/RoutablePages";
import { ExclamationDiamond } from "react-bootstrap-icons";

function FilesForUpdatePage() {
  const [unreleasedVehicleFiles, setUnreleasedVehicleFiles] = useState<
    UnreleasedReleaseVehicleFileViewModel[]
  >([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const routes = useRoutablePages();

  useEffect(() => {
    async function loadUnreleasedVehicleFiles() {
      setLoading(true);
      const data = await releaseAPI.getUnreleasedFiles();

      if (data.ok) {
        setError(undefined);
        setUnreleasedVehicleFiles(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }
    loadUnreleasedVehicleFiles();
  }, []);

  const changeVehicleValidated = async (
    vehicleFileNumber: string,
    type: VehicleFileValidationTypeModel,
    validated: boolean,
  ) => {
    const result = await vehicleFileAPI.setVehicleValidatedFlag(
      vehicleFileNumber,
      type,
      validated,
    );

    if (result.ok) {
      let modified = unreleasedVehicleFiles.map((x) => {
        if (x.number !== vehicleFileNumber) return x;

        if (type === VehicleFileValidationTypeModel.CrsFile)
          return { ...x, crsFileValidated: validated };
        else if (type === VehicleFileValidationTypeModel.ModelInfo)
          return { ...x, modelInfoValidated: validated };
        else if (type === VehicleFileValidationTypeModel.HelperFilesValidation)
          return { ...x, helperFilesValidated: validated };
        else return x;
      });

      setUnreleasedVehicleFiles(modified);
    } else {
      setError(result.problem);
    }
  };

  function getSummary(otherChanges: { [id: string]: number }): string {
    let items: string[] = [];

    const addFunc = (dt: DataFileTypeModel[], description: string) => {
      let count = 0;

      for (let ii = 0; ii < dt.length; ++ii) {
        let key = DataFileTypeModel[dt[ii]];

        if (otherChanges[key]) count += otherChanges[key];
      }

      if (count > 0) {
        items.push(`${count} ${description}${count > 1 ? "s" : ""}`);
      }
    };

    addFunc([DataFileTypeModel.PDFFile], "PDF file");
    addFunc(
      [
        DataFileTypeModel.SelectedFileCrsPic,
        DataFileTypeModel.SelectedFileCheckInfo,
      ],
      "Selected file",
    );
    addFunc(
      [
        DataFileTypeModel.SourceFileOrgPic,
        DataFileTypeModel.SourceFileTechInfoEU,
        DataFileTypeModel.SourceFileTechInfoUSA,
        DataFileTypeModel.SourceFileTechInfoPacific,
        DataFileTypeModel.SourceFileArchive,
      ],
      "Source file",
    );
    addFunc([DataFileTypeModel.ThreeDFile], "3D file");
    addFunc([DataFileTypeModel.QRCodeFile], "QR Code file");

    return items.join(", ");
  }

  return (
    <>
      <div className="main-container-title">
        <h1>Files for Update</h1>
        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />

        {!loading && (
          <Container style={{ fontSize: "80%" }} className="overflow-auto">
            {unreleasedVehicleFiles.length === 0 && <p>Nothing...</p>}

            {unreleasedVehicleFiles.map((vf, index) => (
              <Card key={vf.number} className="mb-3">
                <Card.Header>
                  <Row>
                    <Col className="h3">
                      <a
                        className="link-dark text-decoration-none"
                        href={routes.getVehicleFileDetails(vf.number)}
                      >
                        {vf.number}
                      </a>
                    </Col>
                    <Col>
                      <VehicleFileValidator
                        label="Model Info"
                        enabled={!vf.checkedOutBy}
                        validated={vf.modelInfoValidated}
                        onChangeValidation={(v) =>
                          changeVehicleValidated(
                            vf.number,
                            VehicleFileValidationTypeModel.ModelInfo,
                            v,
                          )
                        }
                      />
                    </Col>
                    <Col>
                      <VehicleFileValidator
                        label="Helper files"
                        enabled={!vf.checkedOutBy}
                        validated={vf.helperFilesValidated}
                        onChangeValidation={(v) =>
                          changeVehicleValidated(
                            vf.number,
                            VehicleFileValidationTypeModel.HelperFilesValidation,
                            v,
                          )
                        }
                      />
                    </Col>
                    <Col xs="auto">
                      {!vf.isNew && vf.dataFiles?.length > 0 && (
                        <ExclamationDiamond
                          size="32"
                          color="red"
                          title="File is not new. It must be finished before the release"
                          style={{ cursor: "help" }}
                        />
                      )}
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body>
                  {vf.dataFiles.length > 0 && (
                    <Table>
                      <thead>
                        <tr>
                          <th style={{ width: "30%" }}>Data File</th>
                          <th style={{ width: "15%" }}>File Version</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vf.dataFiles.map((df) => (
                          <tr key={df.filename}>
                            <td>{df.filename}</td>
                            <td>{df.version}</td>
                            <td>
                              {df.deleted ? "deleted by " : "updated by "}
                              {df.updatedBy}{" "}
                              <DateDisplay date={df.updatedDate} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {vf.hasModelInfoChanges && (
                    <p className="fw-bold">Model info has been modified</p>
                  )}

                  {vf.otherDataFileChanges &&
                    Object.keys(vf.otherDataFileChanges).length > 0 && (
                      <p>
                        <span className="fw-bold">Other modifications: </span>
                        {getSummary(vf.otherDataFileChanges)}
                      </p>
                    )}
                </Card.Body>
                {vf.checkedOutBy && (
                  <Card.Footer>
                    <small>Checked out by {vf.checkedOutBy}</small>
                  </Card.Footer>
                )}
              </Card>
            ))}
          </Container>
        )}
      </div>
    </>
  );
}

export default FilesForUpdatePage;
